import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import type { ColumnDef } from "@tanstack/react-table";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { ClipboardList, FileText, Download } from "lucide-react";
import { useAppLocale } from "@/lib/i18n";
import { sortByLabel } from "@/lib/sort";
import { m } from "@/paraglide/messages.js";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { pickAssortedBadgeClass } from "@/lib/badge-tones";
import { exportToCSV } from "@/lib/export";
import { formatDate, formatStatusLabel } from "@/lib/format";
import { getInventoryStatusBadgeClass } from "@/lib/status-tag-colors";
import { cn } from "@/lib/utils";

const ALL_LOCATIONS = "__ALL__";
const SESSION_GAP_MS = 5 * 60 * 1000; // 5 minutes

type AuditHistoryRow = {
  _id: string;
  createdAt: number;
  userId: string | null;
  auditorName: string | null;
  cargoId: string;
  ociNumber: string | null;
  productName: string | null;
  quantityChanged: number;
  beforeWarehouseBags: number;
  afterWarehouseBags: number;
};

type WarehouseLocationOption = {
  _id: string;
  name: string;
  address: string | null;
  companyName: string | null;
};

function getWarehouseOptionLabel(warehouse: WarehouseLocationOption): string {
  return warehouse.companyName ? `${warehouse.name} — ${warehouse.companyName}` : warehouse.name;
}

type AuditCargoRow = {
  cargoId: string;
  ociNumber: string | null;
  productName: string;
  purchaseNumber: string | null;
  contractNumber: string | null;
  origin: string | null;
  palletLocation: string | null;
  warehouseName: string | null;
  expectedBags: number;
  cargoStatus: string | null;
  warehouseId: string | null;
  unitWeight: number | null;
};

type AuditChecklistRow = AuditCargoRow & {
  id: string;
  actualCount: number;
  variance: number;
  soldDuringAudit: number | null;
  currentSite: number | null;
};

type AuditSession = {
  sessionKey: string;
  date: number;
  auditorName: string | null;
  userId: string | null;
  items: AuditHistoryRow[];
};

type InventoryRecheckSummary = {
  sessionId: string;
  status: "active";
  startedAt: number;
  expiresAt: number;
  startedByName: string | null;
  warehouseId: string | null;
  warehouseName: string | null;
  cargoCount: number;
  impactedOrderCount: number;
  orderDeltaBags: number;
  currentDeltaBags: number;
};

type InventoryRecheckDetailRow = {
  cargoId: string;
  productId: string;
  ociNumber: string | null;
  productName: string | null;
  snapshotAvailableBags: number;
  snapshotReservedBags: number;
  snapshotEffectiveAvailableBags: number;
  snapshotSoldBags: number;
  snapshotWarehouseBags: number;
  currentAvailableBags: number;
  currentReservedBags: number;
  currentEffectiveAvailableBags: number;
  currentSoldBags: number;
  currentWarehouseBags: number;
  currentDeltaBags: number;
  orderDeltaBags: number;
  otherDeltaBags: number;
  impactedOrderCount: number;
  latestOrderAt: number | null;
  latestOrderNumber: string | null;
};

type InventoryRecheckDetails = {
  sessionId: string;
  status: "active";
  startedAt: number;
  expiresAt: number;
  startedByName: string | null;
  warehouseId: string | null;
  warehouseName: string | null;
  items: InventoryRecheckDetailRow[];
};

function groupIntoSessions(rows: AuditHistoryRow[]): AuditSession[] {
  const sessions: AuditSession[] = [];
  for (const row of rows) {
    const lastSession = sessions[sessions.length - 1];
    const lastSessionItem = lastSession?.items[lastSession.items.length - 1];
    if (
      lastSession &&
      lastSessionItem &&
      lastSession.userId === row.userId &&
      row.createdAt >= lastSessionItem.createdAt - SESSION_GAP_MS
    ) {
      lastSession.items.push(row);
    } else {
      sessions.push({
        sessionKey: `${row.userId ?? "anon"}-${row.createdAt}`,
        date: row.createdAt,
        auditorName: row.auditorName,
        userId: row.userId,
        items: [row],
      });
    }
  }
  return sessions;
}

function computeVariance(
  actual: number,
  expected: number,
): { delta: number; percent: number } {
  const delta = actual - expected;
  const percent =
    expected === 0
      ? actual > 0
        ? 100
        : 0
      : Math.abs(delta / expected) * 100;
  return { delta, percent };
}

function VarianceBadge({
  actual,
  expected,
}: {
  actual: number;
  expected: number;
}) {
  const { delta, percent } = computeVariance(actual, expected);
  if (delta === 0) {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        0
      </Badge>
    );
  }
  if (percent <= 5) {
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
        {delta > 0 ? "+" : ""}
        {delta} ({percent.toFixed(1)}%)
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
      {delta > 0 ? "+" : ""}
      {delta} ({percent.toFixed(1)}%)
    </Badge>
  );
}

function formatSignedDelta(value: number): string {
  return `${value > 0 ? "+" : ""}${value}`;
}

function DeltaBadge({ value }: { value: number }) {
  if (value === 0) {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">0</Badge>;
  }

  if (value > 0) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        {formatSignedDelta(value)}
      </Badge>
    );
  }

  if (Math.abs(value) <= 5) {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
        {formatSignedDelta(value)}
      </Badge>
    );
  }

  return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{formatSignedDelta(value)}</Badge>;
}

function matchesAuditChecklistSearch(row: AuditChecklistRow, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return true;
  }

  return [
    row.ociNumber,
    row.productName,
    row.purchaseNumber,
    row.contractNumber,
    row.origin,
    row.palletLocation,
    row.warehouseName,
    row.cargoStatus,
  ].some((value) => String(value ?? "").toLowerCase().includes(normalizedQuery));
}

function resolveExpectedBagsForAuditRow(
  cargo: AuditCargoRow,
  recheckItem?: InventoryRecheckDetailRow | null,
): number {
  return recheckItem?.snapshotWarehouseBags ?? cargo.expectedBags;
}

function StartAuditTab() {
  const { locale } = useAppLocale();
  const [selectedWarehouse, setSelectedWarehouse] =
    React.useState<string>(ALL_LOCATIONS);
  const [actualCounts, setActualCounts] = React.useState<
    Record<string, number>
  >({});
  const [submittedExpectedBags, setSubmittedExpectedBags] = React.useState<
    Record<string, number>
  >({});
  const [checklistSearch, setChecklistSearch] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const warehouseId =
    selectedWarehouse === ALL_LOCATIONS ? undefined : selectedWarehouse;

  const warehouses = useQuery(
    api.warehouseAudit.getWarehouseLocations,
  ) as WarehouseLocationOption[] | undefined;
  const sortedWarehouses = React.useMemo(
    () => sortByLabel(warehouses, getWarehouseOptionLabel, locale),
    [locale, warehouses],
  );

  const cargos = useQuery(api.warehouseAudit.getCargosForAudit, {
    warehouseId: warehouseId as any,
  }) as AuditCargoRow[] | undefined;

  const submitAudit = useMutation(api.warehouseAudit.submitAudit);
  const startInventoryRecheck = useMutation(api.inventoryRecheck.startSession);
  const closeInventoryRecheck = useMutation(api.inventoryRecheck.closeSession);
  const activeRecheckSummary = useQuery(
    api.inventoryRecheck.getActiveSessionSummary,
    {},
  ) as InventoryRecheckSummary | null | undefined;
  const activeRecheckDetails = useQuery(
    api.inventoryRecheck.getActiveSessionDetails,
    {},
  ) as InventoryRecheckDetails | null | undefined;
  const [recheckMutating, setRecheckMutating] = React.useState(false);

  const selectedWarehouseLabel = React.useMemo(() => {
    if (selectedWarehouse === ALL_LOCATIONS) {
      return "All warehouse locations";
    }

    const warehouse = sortedWarehouses.find((entry) => entry._id === selectedWarehouse);
    if (!warehouse) {
      return "Selected warehouse";
    }

    return getWarehouseOptionLabel(warehouse);
  }, [selectedWarehouse, sortedWarehouses]);
  const activeRecheckScopeLabel = activeRecheckSummary
    ? activeRecheckSummary.warehouseName ?? "All warehouse locations"
    : selectedWarehouseLabel;

  React.useEffect(() => {
    if (activeRecheckSummary) {
      const nextWarehouse = activeRecheckSummary.warehouseId ?? ALL_LOCATIONS;
      setSelectedWarehouse((current) => (current === nextWarehouse ? current : nextWarehouse));
    }
  }, [activeRecheckSummary]);

  const recheckDetailMap = React.useMemo(() => {
    if (!activeRecheckDetails?.items) return null;
    const map = new Map<string, InventoryRecheckDetailRow>();
    for (const item of activeRecheckDetails.items) {
      map.set(item.cargoId, item);
    }
    return map;
  }, [activeRecheckDetails]);

  React.useEffect(() => {
    if (!cargos) return;

    setActualCounts((prev) => {
      const initial: Record<string, number> = {};
      for (const cargo of cargos) {
        if (!(cargo.cargoId in prev)) {
          const recheckItem = recheckDetailMap?.get(cargo.cargoId) ?? null;
          initial[cargo.cargoId] = resolveExpectedBagsForAuditRow(cargo, recheckItem);
        }
      }

      return Object.keys(initial).length > 0 ? { ...initial, ...prev } : prev;
    });
  }, [cargos, recheckDetailMap]);

  React.useEffect(() => {
    if (!cargos) return;

    setSubmittedExpectedBags((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const cargo of cargos) {
        const submittedExpected = next[cargo.cargoId];
        if (submittedExpected === undefined) {
          continue;
        }

        const recheckItem = recheckDetailMap?.get(cargo.cargoId) ?? null;
        if (resolveExpectedBagsForAuditRow(cargo, recheckItem) === submittedExpected) {
          delete next[cargo.cargoId];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [cargos, recheckDetailMap]);

  const checklistRows = React.useMemo<AuditChecklistRow[]>(() => {
    if (!cargos) {
      return [];
    }

    return cargos.map((cargo) => {
      const recheckItem = recheckDetailMap?.get(cargo.cargoId) ?? null;
      const expectedBags =
        submittedExpectedBags[cargo.cargoId] ??
        resolveExpectedBagsForAuditRow(cargo, recheckItem);
      const rawActual = actualCounts[cargo.cargoId] ?? expectedBags;
      const actualCount =
        typeof rawActual === "number" &&
        Number.isFinite(rawActual) &&
        rawActual >= 0
          ? Math.floor(rawActual)
          : expectedBags;
      const soldDuringAudit = recheckItem
        ? Math.abs(Math.min(0, recheckItem.orderDeltaBags))
        : null;
      const currentSite = recheckItem
        ? recheckItem.currentEffectiveAvailableBags
        : null;

      return {
        ...cargo,
        expectedBags,
        id: cargo.cargoId,
        actualCount,
        variance: actualCount - expectedBags,
        soldDuringAudit,
        currentSite,
      };
    });
  }, [actualCounts, cargos, recheckDetailMap, submittedExpectedBags]);

  const filteredChecklistRows = React.useMemo(
    () => checklistRows.filter((row) => matchesAuditChecklistSearch(row, checklistSearch)),
    [checklistRows, checklistSearch],
  );

  const changedAuditItems = React.useMemo(
    () =>
      checklistRows
        .filter((row) => row.actualCount !== row.expectedBags)
        .map((row) => ({
          cargoId: row.cargoId as any,
          actualCount: row.actualCount,
        })),
    [checklistRows],
  );

  const isRecheckActive = Boolean(activeRecheckSummary);

  const checklistColumns = React.useMemo<ColumnDef<AuditChecklistRow>[]>(
    () => {
      const columns: ColumnDef<AuditChecklistRow>[] = [
        {
          id: "ociNumber",
          accessorKey: "ociNumber",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.audit_oci_number()} />
          ),
          cell: ({ row }) => (
            <span className="font-mono text-xs">{row.original.ociNumber ?? "-"}</span>
          ),
          meta: {
            label: m.audit_oci_number(),
            placeholder: "Search OCI...",
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "productName",
          accessorKey: "productName",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.product()} />
          ),
          cell: ({ row }) => (
            <div className="space-y-1">
              <span className="text-xs font-medium">{row.original.productName}</span>
              {typeof row.original.unitWeight === "number" ? (
                <p className="text-[11px] text-muted-foreground">{row.original.unitWeight} lbs</p>
              ) : null}
            </div>
          ),
          meta: {
            label: m.product(),
            placeholder: "Search product...",
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "purchaseNumber",
          accessorKey: "purchaseNumber",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label="Cargo Number" />
          ),
          cell: ({ row }) => (
            <span className="text-xs">{row.original.purchaseNumber ?? "-"}</span>
          ),
          meta: {
            label: "Cargo Number",
            placeholder: "Search cargo number...",
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "contractNumber",
          accessorKey: "contractNumber",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label="Contract #" />
          ),
          cell: ({ row }) => (
            <span className="text-xs">{row.original.contractNumber ?? "-"}</span>
          ),
          meta: {
            label: "Contract #",
            placeholder: "Search contract...",
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "origin",
          accessorKey: "origin",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.origin()} />
          ),
          cell: ({ row }) =>
            row.original.origin ? (
              <Badge variant="outline" className={pickAssortedBadgeClass(row.original.origin)}>
                {row.original.origin}
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            ),
          meta: {
            label: m.origin(),
            placeholder: m.search_origin_placeholder(),
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "palletLocation",
          accessorKey: "palletLocation",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.cargos_pallet_location()} />
          ),
          cell: ({ row }) =>
            row.original.palletLocation ? (
              <Badge
                variant="outline"
                className={pickAssortedBadgeClass(row.original.palletLocation)}
              >
                {row.original.palletLocation}
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            ),
          meta: {
            label: m.cargos_pallet_location(),
            placeholder: m.cargos_search_pallet_location_placeholder(),
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "cargoStatus",
          accessorKey: "cargoStatus",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label="Status" />
          ),
          cell: ({ row }) =>
            row.original.cargoStatus ? (
              <Badge
                variant="outline"
                className={getInventoryStatusBadgeClass(row.original.cargoStatus)}
              >
                {formatStatusLabel(row.original.cargoStatus)}
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            ),
          meta: {
            label: "Status",
            placeholder: "Search status...",
            variant: "text" as const,
          },
          enableColumnFilter: true,
        },
        {
          id: "expectedBags",
          accessorKey: "expectedBags",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.audit_expected_bags()} />
          ),
          cell: ({ row }) => (
            <span className="text-xs font-medium tabular-nums">
              {row.original.expectedBags.toLocaleString()}
            </span>
          ),
          meta: { label: m.audit_expected_bags(), variant: "number" as const },
          enableColumnFilter: true,
        },
      ];

      if (isRecheckActive) {
        columns.push(
          {
            id: "soldDuringAudit",
            accessorKey: "soldDuringAudit",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} label="Sold During Audit" />
            ),
            cell: ({ row }) => (
              <span className="text-xs font-medium tabular-nums text-right">
                {row.original.soldDuringAudit !== null
                  ? row.original.soldDuringAudit.toLocaleString()
                  : "-"}
              </span>
            ),
            meta: { label: "Sold During Audit", variant: "number" as const },
            enableColumnFilter: false,
          },
          {
            id: "currentSite",
            accessorKey: "currentSite",
            header: ({ column }) => (
              <DataTableColumnHeader column={column} label="Current Site" />
            ),
            cell: ({ row }) => (
              <span className="text-xs font-medium tabular-nums text-right">
                {row.original.currentSite !== null
                  ? row.original.currentSite.toLocaleString()
                  : "-"}
              </span>
            ),
            meta: { label: "Current Site", variant: "number" as const },
            enableColumnFilter: false,
          },
        );
      }

      columns.push(
        {
          id: "actualCount",
          accessorKey: "actualCount",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.audit_actual_count()} />
          ),
          cell: ({ row }) => (
            <Input
              type="number"
              min={0}
              className="ml-auto h-9 w-24 text-right tabular-nums"
              value={
                Number.isFinite(row.original.actualCount) &&
                row.original.actualCount >= 0
                  ? row.original.actualCount
                  : 0
              }
              onChange={(event) => {
                const nextValue = Number.parseInt(event.target.value, 10);
                if (!Number.isNaN(nextValue) && nextValue >= 0) {
                  setActualCounts((current) => ({
                    ...current,
                    [row.original.cargoId]: nextValue,
                  }));
                }
              }}
            />
          ),
          meta: { label: m.audit_actual_count(), variant: "number" as const },
          enableColumnFilter: false,
        },
        {
          id: "variance",
          accessorKey: "variance",
          header: ({ column }) => (
            <DataTableColumnHeader column={column} label={m.audit_variance()} />
          ),
          cell: ({ row }) => (
            <VarianceBadge
              actual={row.original.actualCount}
              expected={row.original.expectedBags}
            />
          ),
          meta: { label: m.audit_variance(), variant: "number" as const },
          enableColumnFilter: false,
        },
      );

      return columns;
    },
    [isRecheckActive],
  );

  function resetForm() {
    if (!cargos) return;
    const reset: Record<string, number> = {};
    for (const c of cargos) {
      const recheckItem = recheckDetailMap?.get(c.cargoId) ?? null;
      reset[c.cargoId] = resolveExpectedBagsForAuditRow(c);
    }
    setActualCounts(reset);
  }

  async function handleSubmit() {
    if (!cargos || cargos.length === 0) {
      toast.error(m.audit_no_cargos_to_audit());
      return;
    }
    if (changedAuditItems.length === 0) {
      toast.message(m.audit_no_changes_to_submit());
      return;
    }
    setSubmitting(true);
    try {
      const submittedItems = changedAuditItems;
      const result = await submitAudit({
        warehouseId: warehouseId as any,
        items: submittedItems,
      });
      setSubmittedExpectedBags((current) => {
        const next = { ...current };
        for (const item of submittedItems) {
          next[String(item.cargoId)] = item.actualCount;
        }
        return next;
      });
      setActualCounts((current) => {
        const next = { ...current };
        for (const item of submittedItems) {
          next[String(item.cargoId)] = item.actualCount;
        }
        return next;
      });
      const discCount = result.discrepancies.length;
      if (discCount === 0) {
        toast.success(
          m.audit_complete_no_discrepancies({ count: result.auditedCount }),
        );
      } else {
        toast.warning(
          m.audit_complete_with_discrepancies({
            count: result.auditedCount,
            discrepancies: discCount,
          }),
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : m.audit_submission_failed());
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRecheckToggle(nextChecked: boolean) {
    if (recheckMutating) return;

    setRecheckMutating(true);
    try {
      if (nextChecked) {
        const result = await startInventoryRecheck({
          warehouseId: warehouseId as any,
        });
        toast.success(
          `Inventory recheck started. Auto-closes ${formatDate(result.expiresAt)}.`,
        );
      } else {
        if (!activeRecheckSummary?.sessionId) {
          return;
        }

        await closeInventoryRecheck({
          sessionId: activeRecheckSummary.sessionId as any,
          closeReason: "Closed manually from inventory audit.",
        });
        toast.success("Inventory recheck closed.");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to update inventory recheck.");
    } finally {
      setRecheckMutating(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Inventory Recheck</CardTitle>
              <CardDescription>
                Freeze the current storefront snapshot and track bag movement while the warehouse team verifies counts.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3 rounded-full border px-3 py-2">
              <div className="text-right">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {activeRecheckSummary ? "Active" : "Off"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {recheckMutating
                    ? "Updating..."
                    : activeRecheckScopeLabel}
                </p>
              </div>
              <Switch
                checked={Boolean(activeRecheckSummary)}
                onCheckedChange={(checked) => {
                  void handleRecheckToggle(Boolean(checked));
                }}
                disabled={recheckMutating || activeRecheckSummary === undefined}
                aria-label="Toggle inventory recheck"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {activeRecheckSummary ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Started</p>
                    <p className="text-sm font-semibold">{formatDate(activeRecheckSummary.startedAt)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {activeRecheckSummary.startedByName || "Unknown operator"}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Auto-close</p>
                    <p className="text-sm font-semibold">{formatDate(activeRecheckSummary.expiresAt)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {activeRecheckSummary.warehouseName || "All warehouse locations"}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Orders touched</p>
                    <p className="text-2xl font-semibold">{activeRecheckSummary.impactedOrderCount}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {activeRecheckSummary.cargoCount} cargos in scope
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">Live site delta</p>
                    <div className="mt-1">
                      <DeltaBadge value={activeRecheckSummary.currentDeltaBags} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Order-only delta {formatSignedDelta(activeRecheckSummary.orderDeltaBags)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {activeRecheckDetails === undefined ? (
                <p className="text-sm text-muted-foreground">Loading live recheck detail...</p>
              ) : activeRecheckDetails && activeRecheckDetails.items.length > 0 ? (
                <div className="space-y-2">
                  <div>
                    <h3 className="text-sm font-semibold">Live delta worksheet</h3>
                    <p className="text-sm text-muted-foreground">
                      Compare the opening storefront snapshot against the current site numbers and the order-driven movement captured during this recheck.
                    </p>
                  </div>
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>OCI</TableHead>
                          <TableHead>{m.product()}</TableHead>
                          <TableHead className="text-right">Snapshot site</TableHead>
                          <TableHead className="text-right">Current site</TableHead>
                          <TableHead className="text-right">Live delta</TableHead>
                          <TableHead className="text-right">Order delta</TableHead>
                          <TableHead className="text-right">Other delta</TableHead>
                          <TableHead className="text-right">Orders</TableHead>
                          <TableHead>Latest order</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeRecheckDetails.items.map((item) => (
                          <TableRow key={item.cargoId}>
                            <TableCell className="font-mono text-xs">{item.ociNumber ?? "-"}</TableCell>
                            <TableCell>{item.productName ?? "-"}</TableCell>
                            <TableCell className="text-right">{item.snapshotEffectiveAvailableBags}</TableCell>
                            <TableCell className="text-right">{item.currentEffectiveAvailableBags}</TableCell>
                            <TableCell className="text-right">
                              <DeltaBadge value={item.currentDeltaBags} />
                            </TableCell>
                            <TableCell className="text-right">
                              <DeltaBadge value={item.orderDeltaBags} />
                            </TableCell>
                            <TableCell className="text-right">
                              <DeltaBadge value={item.otherDeltaBags} />
                            </TableCell>
                            <TableCell className="text-right">{item.impactedOrderCount}</TableCell>
                            <TableCell>
                              {item.latestOrderNumber ? (
                                <div className="space-y-1">
                                  <div className="font-mono text-xs">{item.latestOrderNumber}</div>
                                  {item.latestOrderAt ? (
                                    <div className="text-xs text-muted-foreground">
                                      {formatDate(item.latestOrderAt)}
                                    </div>
                                  ) : null}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">No order movement yet</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No cargos were captured in this recheck scope.
                </p>
              )}
            </>
          ) : (
            <div className="rounded-md border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
              Start the recheck when warehouse counting begins. The current scope is <span className="font-medium text-foreground">{selectedWarehouseLabel}</span>, and the session will auto-close after 24 hours unless you switch it off sooner.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{m.audit_warehouse_selector()}</CardTitle>
          <CardDescription>
            {m.audit_warehouse_selector_description()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={selectedWarehouse}
            onValueChange={setSelectedWarehouse}
            disabled={Boolean(activeRecheckSummary) || recheckMutating}
          >
            <SelectTrigger className="w-[320px]">
              <SelectValue placeholder={m.audit_select_warehouse()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_LOCATIONS}>{m.audit_all_locations()}</SelectItem>
              {sortedWarehouses.map((warehouse) => (
                <SelectItem key={warehouse._id} value={warehouse._id}>
                  {getWarehouseOptionLabel(warehouse)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{m.audit_cargo_checklist()}</CardTitle>
              <CardDescription className="space-y-1">
                <span>{m.audit_cargo_checklist_description()}</span>
                <span className="block text-muted-foreground">
                  {m.audit_checklist_changed_only()}
                </span>
              </CardDescription>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={
                submitting ||
                checklistRows.length === 0 ||
                changedAuditItems.length === 0
              }
            >
              {submitting ? m.audit_submitting() : m.audit_submit()}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {cargos === undefined ? (
            <p className="text-sm text-muted-foreground">{m.audit_loading_cargos()}</p>
          ) : checklistRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {m.audit_no_active_cargos()}
            </p>
          ) : (
            <>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <Input
                  value={checklistSearch}
                  onChange={(event) => setChecklistSearch(event.target.value)}
                  placeholder="Search OCI, product, cargo number, contract, pallet location..."
                  className="w-full lg:max-w-sm"
                />
                <p className="text-sm text-muted-foreground">
                  Showing {filteredChecklistRows.length} of {checklistRows.length} cargos
                </p>
              </div>

              {filteredChecklistRows.length > 0 ? (
                <InternalEntityDataTable
                  data={filteredChecklistRows}
                  columns={checklistColumns}
                  pageSize={20}
                  enableSelection={false}
                  getRowClassName={(row) =>
                    row.original.actualCount !== row.original.expectedBags
                      ? cn(
                          "bg-amber-50/70 hover:bg-amber-100/80",
                          "data-[state=selected]:bg-amber-100",
                        )
                      : undefined
                  }
                  initialSorting={[
                    { id: "productName", desc: false },
                    { id: "contractNumber", desc: false },
                    { id: "purchaseNumber", desc: false },
                    { id: "ociNumber", desc: false },
                  ]}
                />
              ) : (
                <div className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                  No cargos match your search.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SessionDetailsModal({
  session,
  open,
  onOpenChange,
}: {
  session: AuditSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  useAppLocale();
  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{m.audit_session_details()}</DialogTitle>
          <DialogDescription>
            {m.audit_session_description({
              date: formatDate(session.date),
              auditorName: session.auditorName ?? "Unknown",
            })}
          </DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{m.audit_oci_number()}</TableHead>
              <TableHead>{m.product()}</TableHead>
              <TableHead className="text-right">{m.audit_expected()}</TableHead>
              <TableHead className="text-right">{m.audit_actual()}</TableHead>
              <TableHead className="text-right">{m.audit_delta()}</TableHead>
              <TableHead className="text-right">{m.audit_percent_variance()}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {session.items.map((item) => {
              const { delta, percent } = computeVariance(
                item.afterWarehouseBags,
                item.beforeWarehouseBags,
              );
              return (
                <TableRow key={item._id}>
                  <TableCell className="font-mono text-sm">
                    {item.ociNumber ?? "—"}
                  </TableCell>
                  <TableCell>{item.productName ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    {item.beforeWarehouseBags}
                  </TableCell>
                  <TableCell className="text-right">
                    {item.afterWarehouseBags}
                  </TableCell>
                  <TableCell className="text-right">
                    {delta > 0 ? "+" : ""}
                    {delta}
                  </TableCell>
                  <TableCell className="text-right">
                    {percent.toFixed(1)}%
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {m.audit_close()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportModal({
  sessions,
  initialSession,
  open,
  onOpenChange,
}: {
  sessions: AuditSession[];
  initialSession: AuditSession | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  useAppLocale();
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open && initialSession) {
      setSelectedKey(initialSession.sessionKey);
    }
  }, [open, initialSession]);

  const session =
    sessions.find((s) => s.sessionKey === selectedKey) ?? initialSession;

  function handleExport() {
    if (!session) return;
    const rows = session.items.map((item) => {
      const { delta, percent } = computeVariance(
        item.afterWarehouseBags,
        item.beforeWarehouseBags,
      );
      return {
        ociNumber: item.ociNumber ?? "",
        productName: item.productName ?? "",
        expectedBags: item.beforeWarehouseBags,
        actualBags: item.afterWarehouseBags,
        delta,
        percentVariance: `${percent.toFixed(1)}%`,
      };
    });
    exportToCSV(
      rows,
      [
        { key: "ociNumber", label: m.audit_oci_number() },
        { key: "productName", label: m.audit_product_name() },
        { key: "expectedBags", label: m.audit_expected_bags() },
        { key: "actualBags", label: m.audit_actual_bags() },
        { key: "delta", label: m.audit_delta() },
        { key: "percentVariance", label: m.audit_percent_variance() },
      ],
      `audit-report-${new Date(session.date).toISOString().slice(0, 10)}.csv`,
    );
  }

  const highVarianceItems = session
    ? session.items.filter((item) => {
        const { percent } = computeVariance(
          item.afterWarehouseBags,
          item.beforeWarehouseBags,
        );
        return percent > 5;
      })
    : [];

  const discrepancies = session
    ? session.items.filter((item) => item.quantityChanged !== 0)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{m.audit_generate_report()}</DialogTitle>
          <DialogDescription>
            {m.audit_generate_report_description()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p id="audit-session-select-label" className="mb-1 block text-sm font-medium">
              {m.audit_session()}
            </p>
            <Select
              value={selectedKey ?? ""}
              onValueChange={setSelectedKey}
            >
              <SelectTrigger aria-labelledby="audit-session-select-label">
                <SelectValue placeholder={m.audit_select_session()} />
              </SelectTrigger>
              <SelectContent>
                {sessions.map((s) => (
                  <SelectItem key={s.sessionKey} value={s.sessionKey}>
                    {m.audit_session_option({
                      date: formatDate(s.date),
                      auditorName: s.auditorName ?? "Unknown",
                      count: s.items.length,
                    })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {session && (
            <div className="rounded-md border p-4 space-y-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-muted-foreground">{m.audit_date()}</span>
                <span>{formatDate(session.date)}</span>
                <span className="text-muted-foreground">{m.audit_auditor()}</span>
                <span>{session.auditorName ?? "Unknown"}</span>
                <span className="text-muted-foreground">{m.audit_total_items()}</span>
                <span>{session.items.length}</span>
                <span className="text-muted-foreground">{m.audit_discrepancies()}</span>
                <span>{discrepancies.length}</span>
              </div>

              {highVarianceItems.length > 0 && (
                <div className="pt-2">
                  <p className="text-sm font-medium mb-1">
                    {m.audit_high_variance_items({ count: highVarianceItems.length })}
                  </p>
                  <ul className="text-sm space-y-1">
                    {highVarianceItems.map((item) => {
                      const { delta, percent } = computeVariance(
                        item.afterWarehouseBags,
                        item.beforeWarehouseBags,
                      );
                      return (
                        <li key={item._id} className="text-red-600">
                          {item.ociNumber ?? "—"} / {item.productName ?? "—"}:{" "}
                          {delta > 0 ? "+" : ""}
                          {delta} ({percent.toFixed(1)}%)
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {m.cancel()}
          </Button>
          <Button onClick={handleExport} disabled={!session}>
            <Download className="w-4 h-4 mr-2" />
            {m.audit_export_csv()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditHistoryTab() {
  useAppLocale();
  const [detailsSession, setDetailsSession] =
    React.useState<AuditSession | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [reportSession, setReportSession] =
    React.useState<AuditSession | null>(null);
  const [reportOpen, setReportOpen] = React.useState(false);

  const history = useQuery(api.warehouseAudit.getAuditHistory, {
    limit: 200,
  }) as AuditHistoryRow[] | undefined;

  const sessions = React.useMemo(() => {
    if (!history) return [];
    return groupIntoSessions(history);
  }, [history]);

  function openDetails(session: AuditSession) {
    setDetailsSession(session);
    setDetailsOpen(true);
  }

  function openReport(session: AuditSession) {
    setReportSession(session);
    setReportOpen(true);
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{m.audit_history()}</CardTitle>
              <CardDescription>
                {m.audit_history_description()}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setReportSession(sessions[0] ?? null);
                setReportOpen(true);
              }}
              disabled={sessions.length === 0}
            >
              <FileText className="w-4 h-4 mr-2" />
              {m.audit_generate_report()}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {history === undefined ? (
            <p className="text-sm text-muted-foreground">
              {m.audit_loading_history()}
            </p>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {m.audit_no_sessions_found()}
            </p>
          ) : (
            <Table>
              <TableHeader>
                 <TableRow>
                  <TableHead>{m.audit_date()}</TableHead>
                  <TableHead>{m.audit_auditor()}</TableHead>
                  <TableHead className="text-right">{m.audit_items_audited()}</TableHead>
                  <TableHead className="text-right">{m.audit_discrepancies()}</TableHead>
                  <TableHead className="text-right">{m.audit_actions()}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => {
                  const discrepancies = session.items.filter(
                    (item) => item.quantityChanged !== 0,
                  ).length;
                  return (
                    <TableRow key={session.sessionKey}>
                      <TableCell>{formatDate(session.date)}</TableCell>
                      <TableCell>
                        {session.auditorName ?? "Unknown"}
                      </TableCell>
                      <TableCell className="text-right">
                        {session.items.length}
                      </TableCell>
                      <TableCell className="text-right">
                        {discrepancies > 0 ? (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                            {discrepancies}
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            0
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetails(session)}
                          >
                            {m.audit_view_details()}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReport(session)}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            {m.audit_report()}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SessionDetailsModal
        session={detailsSession}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />

      <ReportModal
        sessions={sessions}
        initialSession={reportSession}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />
    </div>
  );
}

export default function WarehouseAuditPage() {
  useAppLocale();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList className="w-6 h-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {m.audit_warehouse_audit()}
          </h1>
          <p className="text-sm text-muted-foreground">
            {m.audit_warehouse_audit_description()}
          </p>
        </div>
      </div>

      <Tabs defaultValue="audit">
        <TabsList>
          <TabsTrigger value="audit">{m.audit_start_audit()}</TabsTrigger>
          <TabsTrigger value="history">{m.audit_history()}</TabsTrigger>
        </TabsList>
        <TabsContent value="audit" className="mt-4">
          <StartAuditTab />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <AuditHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
